import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress, Typography, Paper } from '@mui/material'
import { ErrorOutline } from '@mui/icons-material'

import CampaignDetail from '../components/CampaignDetail'
import { useCampaigns, useToast } from '../hooks'
import { useGlobalStoreActions } from '../../store/globalStore'

const MissingParametersError = () => {
  const { t } = useTranslation()

  return (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          p: 8,
          maxWidth: 600,
          width: '100%',
          backgroundColor: '#fef7f7',
          border: '2px solid #ffcdd2',
          borderRadius: 3
        }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#f44336',
              borderRadius: '50%',
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ErrorOutline sx={{ color: 'white', fontSize: 48 }} />
          </Box>
        </Box>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: '#d32f2f'
          }}
        >
          {t('mailTemplate.campaigns.accessErrorTitle')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#424242',
            lineHeight: 1.6
          }}
        >
          {t('mailTemplate.campaigns.campaignNotFoundError')}
        </Typography>
      </Paper>
    </Box>
  </Box>
  )
}

const LoadingState = () => (
  <Box sx={{ p: 2 }}>
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 400
    }}>
      <CircularProgress size={60} />
    </Box>
  </Box>
)

const CampaignNotFound = () => {
  const { t } = useTranslation()

  return (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        px: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          p: 8,
          maxWidth: 600,
          width: '100%',
          backgroundColor: '#fef7f7',
          border: '2px solid #ffcdd2',
          borderRadius: 3
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: '#d32f2f'
          }}
        >
          {t('mailTemplate.campaigns.campaignNotFound')}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#424242',
            lineHeight: 1.6
          }}
        >
          {t('mailTemplate.campaigns.campaignNotFoundDescription')}
        </Typography>
      </Paper>
    </Box>
  </Box>
  )
}

const CampaignDetailPage = () => {
  const { t } = useTranslation()

  const { showError } = useToast()

  const [searchParams] = useSearchParams()

  const [campaign, setCampaign] = useState(null)
  const [hasFetched, setHasFetched] = useState(false)

  const { setCampaignStatus } = useGlobalStoreActions()

  const b2bAccountId = searchParams.get('b2bAccountId')
  const b2bCampaignId = searchParams.get('b2bCampaignId')

  const { campaigns, fetchCampaigns, isLoading } = useCampaigns(b2bAccountId)

  const refreshCampaign = useCallback(async () => {
    try {
      await fetchCampaigns()
    } catch (error) {
      console.error('Failed to refresh campaign data:', error)
    }
  }, [fetchCampaigns])

  useEffect(() => {
    const loadCampaigns = async () => {
      if (b2bAccountId && !hasFetched && !isLoading) {
        try {
          await fetchCampaigns()
        } catch (error) {
          showError(t('mailTemplate.campaigns.errors.failedToLoadCampaigns'))
        } finally {
          setHasFetched(true)
        }
      }
    }

    loadCampaigns()
  }, [b2bAccountId, hasFetched, isLoading, fetchCampaigns, showError, t])

  useEffect(() => {
    if (campaigns && b2bCampaignId) {
      const foundCampaign = campaigns.find(c => c.id === b2bCampaignId)
      setCampaign(foundCampaign)
      if (foundCampaign?.status) {
        setCampaignStatus(b2bCampaignId, foundCampaign.status)
      }
    }
  }, [campaigns, b2bCampaignId, setCampaignStatus])

  if (!b2bAccountId || !b2bCampaignId) {
    return <MissingParametersError />
  }

  if (isLoading || (!hasFetched && b2bAccountId)) {
    return <LoadingState />
  }
  if (!campaign) {
    return <CampaignNotFound />
  }

  return <CampaignDetail campaign={campaign} onCampaignUpdate={refreshCampaign} />
}

export default CampaignDetailPage
